import React, { useState, useEffect, useContext } from 'react';
import {
  Container, Box, Typography, Paper, IconButton, Grid, Chip, alpha,
  Dialog, DialogTitle, DialogContent, DialogActions, Button, CircularProgress, Alert
} from '@mui/material';
import { format, startOfMonth, endOfMonth, startOfWeek, endOfWeek, addDays, subMonths, addMonths, eachDayOfInterval, isToday, isPast, endOfDay } from 'date-fns';
import { fr } from 'date-fns/locale/fr';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import AuthContext from '../context/AuthContext';
import axiosInstance from '../api/axiosConfig';
import { getBrusselsDate } from '../utils/dateUtils';

const statusStyles = {
  wait: { label: 'En attente', color: 'warning.main', bgColor: alpha('#ff9800', 0.2) },
  confirm: { label: 'Confirmé', color: 'success.main', bgColor: alpha('#4caf50', 0.2) },
  refuse: { label: 'Refusé', color: 'error.main', bgColor: alpha('#f44336', 0.2) },
  force: { label: 'Forcé', color: 'success.main', bgColor: alpha('#4caf50', 0.2) },
};

const ScheduleDetailDialog = ({ open, onClose, schedule, onStatusChange }) => {
  if (!schedule) return null;

  const handleStatusUpdate = async (newStatus) => {
    try {
      await axiosInstance.put(`/schedule/me/${schedule.id}/status`, { status: newStatus });
      onStatusChange(schedule.id, newStatus);
      onClose();
    } catch (error) {
      console.error("Failed to update status", error);
      alert("Erreur lors de la mise à jour du statut.");
    }
  };

  const style = statusStyles[schedule.confirmationStatus];

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Détail de l'horaire</DialogTitle>
      <DialogContent>
        <Typography variant="h6">{schedule.Task.name}</Typography>
        <Typography gutterBottom>
          Le {format(new Date(schedule.date), 'eeee d MMMM yyyy', { locale: fr })}
        </Typography>
        <Typography>
          De <strong>{schedule.startTime.substring(0, 5)}</strong> à <strong>{schedule.endTime.substring(0, 5)}</strong>
        </Typography>
        {schedule.confirmationStatus !== 'force' && (
          <Typography component="div" sx={{ mt: 2 }}>
            Statut : <Chip label={style.label} sx={{ backgroundColor: style.bgColor, color: style.color, fontWeight: 'bold' }} size="small" />
          </Typography>
        )}
        <Typography variant="caption" color="text.secondary">
          Créé le : {format(new Date(schedule.createdAt), 'd MMMM yyyy à HH:mm', { locale: fr })}
        </Typography>
      </DialogContent>
      <DialogActions>
        {schedule.confirmationStatus === 'wait' && (
          <>
            <Button onClick={() => handleStatusUpdate('refuse')} color="error">Refuser</Button>
            <Button onClick={() => handleStatusUpdate('confirm')} variant="contained" color="success">Confirmer</Button>
          </>
        )}
        <Button onClick={onClose}>Fermer</Button>
      </DialogActions>
    </Dialog>
  );
};

const MySchedule = () => {
  const [currentMonth, setCurrentMonth] = useState(getBrusselsDate());
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedSchedule, setSelectedSchedule] = useState(null);

  const { currentUser } = useContext(AuthContext);

  useEffect(() => {
    const fetchSchedules = async () => {
      if (!currentUser) return;
      setLoading(true);
      try {
        // Déterminer la plage de dates exacte affichée dans le calendrier
        const monthStart = startOfMonth(currentMonth);
        const monthEnd = endOfMonth(currentMonth);
        const calendarStart = startOfWeek(monthStart, { weekStartsOn: 1 });
        const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });

        // Utiliser cette plage pour l'appel API
        const response = await axiosInstance.get(`/schedule/me?startDate=${format(calendarStart, 'yyyy-MM-dd')}&endDate=${format(calendarEnd, 'yyyy-MM-dd')}`);
        setSchedules(response.data);
      } catch (err) {
        setError('Erreur lors du chargement de vos horaires.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchSchedules();
  }, [currentUser, currentMonth]);

  const handlePrevMonth = () => setCurrentMonth(subMonths(currentMonth, 1));
  const handleNextMonth = () => setCurrentMonth(addMonths(currentMonth, 1));

  const handleOpenDialog = (schedule) => setSelectedSchedule(schedule);
  const handleCloseDialog = () => setSelectedSchedule(null);

  const handleStatusChange = (scheduleId, newStatus) => {
    setSchedules(schedules.map(s => s.id === scheduleId ? { ...s, confirmationStatus: newStatus } : s));
  };

  const renderCalendar = () => {
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(currentMonth);
    const calendarStart = startOfWeek(monthStart, { weekStartsOn: 1 });
    const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });

    const days = eachDayOfInterval({ start: calendarStart, end: calendarEnd });
    const weekDays = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];

    return (
      // Ajout d'un conteneur pour permettre le défilement horizontal sur les petits écrans
      <Box sx={{ overflowX: 'auto' }}>
        <Paper sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, 1fr)',
          border: '1px solid',
          borderColor: 'divider',
          // Largeur minimale pour assurer que le calendrier ne soit pas écrasé
        }}>
          {/* En-têtes de semaine */}
          {weekDays.map(day => (
            <Box key={day} sx={{ textAlign: 'center', p: 1, borderBottom: '1px solid', borderColor: 'divider', backgroundColor: 'grey.100' }}>
              <Typography
                variant="subtitle2"
                sx={{
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: { xs: 'none', sm: 'block' }, userSelect: 'none'
                }}
              >{day}</Typography>
              {/* Version abrégée pour les très petits écrans */}
              <Typography variant="subtitle2" sx={{ display: { xs: 'block', sm: 'none' } }}>{day.substring(0, 3)}</Typography>
            </Box>
          ))}

          {/* Jours du calendrier */}
          {days.map((day, index) => {
            const dateString = format(day, 'yyyy-MM-dd');
            const schedulesForDay = schedules.filter(s => s.date === dateString);
            const isCurrentMonth = day.getMonth() === currentMonth.getMonth();
            const isCurrentDay = isToday(day);
            const isDayInThePast = isPast(endOfDay(day));

            return (
              <Box
                key={dateString}
                sx={{
                  borderTop: '1px solid',
                  borderRight: (index + 1) % 7 !== 0 ? '1px solid' : 'none',
                  borderColor: 'divider',
                  minHeight: 70, // Hauteur légèrement réduite pour un meilleur affichage mobile
                  p: 0.5, // Padding réduit
                  opacity: isDayInThePast ? 0.6 : (isCurrentMonth ? 1 : 0.6),
                  boxShadow: isCurrentDay ? `inset 0 0 0 3px #4caf50` : 'none',
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5, userSelect: 'none' }}>
                  {format(day, 'd')}
                </Typography>
                {schedulesForDay.map(schedule => {
                  const style = statusStyles[schedule.confirmationStatus];
                  return (
                    <Chip
                      key={schedule.id}
                      label={
                        <>
                          {/* Affichage sur une ligne pour les grands écrans */}
                          <Box sx={{ display: { xs: 'none', lg: 'block' } }}>
                            {`${schedule.startTime.substring(0, 5)} - ${schedule.endTime.substring(0, 5)}`}
                          </Box>
                          {/* Affichage sur deux lignes pour les écrans < 1200px */}
                          <Box sx={{ display: { xs: 'block', lg: 'none' }, textAlign: 'center' }}>
                            <Typography
                              variant="caption"
                              fontWeight='bold'
                              component="div"
                              lineHeight={1.1}
                              fontSize="0.6rem"
                              sx={{ fontSize: { xs: '0.6rem', sm: '0.75rem' } }}>
                              {schedule.startTime.substring(0, 5)} <br /> {schedule.endTime.substring(0, 5)}
                            </Typography>
                          </Box>
                        </>
                      }
                      onClick={() => handleOpenDialog(schedule)}
                      sx={{
                        backgroundColor: style.bgColor, color: style.color, fontWeight: 'bold',
                        width: '100%', justifyContent: 'center', mb: 0.5, borderRadius: 1,
                        '& .MuiChip-label': {
                          padding: '0px',
                        }
                      }}
                      size="medium"
                    />
                  );
                })}
              </Box>
            );
          })}
        </Paper>
      </Box>
    );
  };

  return (
    <Container maxWidth="lg">
      <Paper sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', p: 1, mb: 2, userSelect: 'none' }}>
        <IconButton onClick={handlePrevMonth}>
          <ChevronLeftIcon />
        </IconButton>
        <Typography variant="h5" sx={{ mx: 3, textTransform: 'capitalize' }}>
          {format(currentMonth, 'MMMM yyyy', { locale: fr })}
        </Typography>
        <IconButton onClick={handleNextMonth}>
          <ChevronRightIcon />
        </IconButton>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        renderCalendar()
      )}

      <ScheduleDetailDialog
        open={!!selectedSchedule}
        onClose={handleCloseDialog}
        schedule={selectedSchedule}
        onStatusChange={handleStatusChange}
      />

      {/* Section Export iCal */}
      <Box sx={{ mt: 4, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
        <Typography variant="h6" gutterBottom>Export iCal</Typography>
        <Typography variant="body2" paragraph>
          Utilisez cette URL pour synchroniser votre planning avec votre calendrier personnel (Google Calendar, Outlook, Apple Calendar, etc.).
        </Typography>
        {currentUser?.icalToken ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Paper
              component="code"
              sx={{ p: 1, bgcolor: 'grey.100', flexGrow: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontFamily: 'monospace' }}
            >
              {`${process.env.NODE_ENV === 'production' ? 'https://api.madispo.com' : 'http://localhost:3000'}/public/ical/${currentUser.icalToken}`}
            </Paper>
            <Button
              variant="outlined"
              onClick={() => {
                const url = `${process.env.NODE_ENV === 'production' ? 'https://api.madispo.com' : 'http://localhost:3000'}/public/ical/${currentUser.icalToken}`;
                navigator.clipboard.writeText(url);
                alert('URL copiée !');
              }}
            >
              Copier
            </Button>
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary">
            Chargement de votre lien d'export...
          </Typography>
        )}
      </Box>
    </Container>
  );
};

export default MySchedule;